import React, { useState, useEffect, useCallback } from "react";
import { DragDropContext, Draggable, DraggableLocation, Droppable, DropResult, ResponderProvided } from "@hello-pangea/dnd";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import { IOnUpdateCatalogDTO } from "../../../../models/DTO/IOnUpdateCatalogDTO";
import { IUpdateCatalogDTO } from "../../../../models/DTO/IUpdateCatalogDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import ICatalog from "../../../../models/ICatalog";
import IInventoryRowConfig from "../../../../models/IInventoryRowConfig";
import INotification from "../../../../models/INotification";
import InventoryRowColPropertyEnum from "../../../../models/InventoryRowColPropertyEnum";
import ISupplierModel from "../../../../models/ISupplierModel";
import IUserUI from "../../../../models/IUserUI";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../../models/NotificationPositionEnum";
import OwButton from "../../../shared/OwButton/OwButton";
import OwCheckbox from "../../../shared/OwCheckbox/OwCheckbox";
import OwInputWrapper from "../../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import WhatsThis from "../../../shared/WhatsThis/WhatsThis";
import "./InventoryRowConfig.scss";
import { Socket } from "socket.io-client";

type Props = {
    socket: Socket;
    catalogModel: ICatalog;
    toggleInventoryRowConfig: () => void;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
};

const InventoryRowConfig: React.FC<Props> = ({ socket, catalogModel, toggleInventoryRowConfig, userModel, supplierModel, notifyFn }) => {
    const getDefaultInventoryRowConfig = (): IInventoryRowConfig[] => {
        return [
            {
                property: InventoryRowColPropertyEnum.ACTIONS,
                sequence: 1,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.NAME,
                sequence: 2,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.SKU,
                sequence: 3,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.DESCRIPTION,
                sequence: 4,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.MSRP,
                sequence: 5,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.PRICE_GROUP,
                sequence: 6,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.UOM,
                sequence: 7,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.RESERVED_QTY_ON_HAND,
                sequence: 8,
                func: "",
                isSupplier: true,
            },
            {
                property: InventoryRowColPropertyEnum.QTY_ON_HAND,
                sequence: 9,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.QTY_BACKORDERED,
                sequence: 10,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.QTY_HELD_FOR_BACKORDERS,
                sequence: 10,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.BACKORDER_LIMIT,
                sequence: 10,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.LOCATION,
                sequence: 11,
                func: "",
            },
        ];
    };

    const getInventoryRowConfig = useCallback(() => {
        let inventory_row_config = catalogModel.inventory_row_config || [];

        if (!inventory_row_config.length) {
            inventory_row_config = getDefaultInventoryRowConfig();
        }

        return inventory_row_config;
    }, [catalogModel.inventory_row_config]);

    const getAllInventoryRowConfig = (): IInventoryRowConfig[] => {
        return [
            {
                property: InventoryRowColPropertyEnum.ACTIONS,
                sequence: 1,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.NAME,
                sequence: 2,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.SKU,
                sequence: 3,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.DESCRIPTION,
                sequence: 4,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.MSRP,
                sequence: 5,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.PRICE_GROUP,
                sequence: 6,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.UOM,
                sequence: 7,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.RESERVED_QTY_ON_HAND,
                sequence: 8,
                func: "",
                isSupplier: true,
            },
            {
                property: InventoryRowColPropertyEnum.QTY_ON_HAND,
                sequence: 9,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.QTY_INCOMING,
                sequence: 10,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.QTY_BACKORDERED,
                sequence: 11,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.QTY_HELD_FOR_BACKORDERS,
                sequence: 11,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.BACKORDER_LIMIT,
                sequence: 11,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.LAST_UPDATED,
                sequence: 11,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.PACK,
                sequence: 12,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.PURCHASE_LINK,
                sequence: 13,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.LOCATION,
                sequence: 14,
                func: "",
            },
            {
                property: InventoryRowColPropertyEnum.MAP,
                sequence: 15,
                func: "",
            },
        ];
    };

    const getAvailableItems = useCallback(() => {
        const inventory_row_config = getInventoryRowConfig();
        const all_inventory_row_config = getAllInventoryRowConfig();
        const filtered_inventory_row_config: IInventoryRowConfig[] = [];

        for (const all_row_config_item of all_inventory_row_config) {
            let found = false;

            for (const selected_row_config_item of inventory_row_config) {
                if (selected_row_config_item.property === all_row_config_item.property) {
                    found = true;
                }
            }

            if (!found) {
                filtered_inventory_row_config.push(all_row_config_item);
            }
        }

        return filtered_inventory_row_config;
    }, [getInventoryRowConfig]);

    const [selectedItems, setSelectedItems] = useState<IInventoryRowConfig[]>(getInventoryRowConfig());
    const [availableItems, setAvailableItems] = useState<IInventoryRowConfig[]>(getAvailableItems());
    const [hasUnsavedChange, setHasUnsavedChange] = useState(false);
    const [catalogModelState, setCatalogModelState] = useState<ICatalog>(catalogModel);
    const [saveActive, setSaveActive] = useState(false);

    const onUpdateCatalog = useCallback(
        (data: IOnUpdateCatalogDTO) => {
            const notification: INotification = {
                message: "inventory_row_config_updated",
                level: NotificationLevelEnum.success,
                position: NotificationPositionEnum.bc,
            };

            notifyFn(null, notification);

            setCatalogModelState(data.catalog);
            setHasUnsavedChange(false);
            setSaveActive(false);
        },
        [notifyFn]
    );

    useEffect(() => {
        socket.on(EndpointsEnum.CatalogsOnUpdateCatalog, onUpdateCatalog);

        return () => {
            socket.removeListener(EndpointsEnum.CatalogsOnUpdateCatalog, onUpdateCatalog);
        };
    }, [socket, onUpdateCatalog]);

    const reorder = (list: IInventoryRowConfig[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const move = (
        source: IInventoryRowConfig[],
        destination: IInventoryRowConfig[],
        droppableSource: DraggableLocation,
        droppableDestination: DraggableLocation
    ) => {
        const sourceClone = Array.from(source);
        const destClone = Array.from(destination);
        const [removed] = sourceClone.splice(droppableSource.index, 1);

        destClone.splice(droppableDestination.index, 0, removed);

        const result: any = {
            sourceItems: sourceClone,
            destItems: destClone,
        };

        return result;
    };

    const onDragEnd = (result: DropResult, provided: ResponderProvided) => {
        const { source, destination } = result;

        if (!destination) {
            return;
        }

        if (source.droppableId === destination.droppableId) {
            const list = source.droppableId === "selectedItems" ? selectedItems : availableItems;

            // We don't allow anything other than Preliminary to be in the first spot
            if (destination.index === 0 || source.index === 0) {
                return;
            }

            const items = reorder(list, source.index, destination.index);

            if (source.droppableId === "selectedItems") {
                setSelectedItems(items);
            } else {
                setAvailableItems(items);
            }
        } else {
            const sourceItems: IInventoryRowConfig[] = source.droppableId === "selectedItems" ? selectedItems : availableItems;
            const destinationItems: IInventoryRowConfig[] = destination.droppableId === "selectedItems" ? selectedItems : availableItems;

            const result = move(sourceItems, destinationItems, source, destination);

            if (source.droppableId === "selectedItems") {
                setSelectedItems(result.sourceItems);
                setAvailableItems(result.destItems);
            } else {
                setAvailableItems(result.sourceItems);
                setSelectedItems(result.destItems);
            }
        }

        setHasUnsavedChange(true);
    };

    const saveCatalogRowConfig = () => {
        const payload: IUpdateCatalogDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            catalogGuid: catalogModelState.guid,
            active: !!catalogModelState.active,
            version: catalogModelState.version,
            name: catalogModelState.name,
            inventory_row_config: selectedItems,
        };

        socket.emit("updateCatalog", payload);

        setSaveActive(true);
    };

    const handleChange = (e: any, index: number, columnDetail: IInventoryRowConfig) => {
        const newSelectedItems = [...selectedItems];
        newSelectedItems[index].isSupplier = e.target.checked;
        setSelectedItems(newSelectedItems);
        setHasUnsavedChange(true);
    };

    const handleTitleOverrideChange = (e: any, index: number, columnDetail: IInventoryRowConfig) => {
        const newSelectedItems = [...selectedItems];
        newSelectedItems[index].titleOverride = e.target.value;
        setSelectedItems(newSelectedItems);
        setHasUnsavedChange(true);
    };

    return (
        <div className="w-100 InventoryRowConfig">
            <div>
                <div>
                    <div className={"d-flex"}>
                        <div className={"d-flex align-items-center"}>
                            <OwTitle className={"me-auto"} leftIconName={"fad fa-columns"} title={"inventory_row_config"} tag={"h3"} />
                            <div className={"ms-2"}>
                                <OwButton title={"save"} fn={saveCatalogRowConfig} active={saveActive} disableOnActive={true} />
                                {hasUnsavedChange ? (
                                    <OwLabel title={"pending_changes"} className={"text-warning ms-2"} noBottomMargin={true} />
                                ) : null}
                            </div>
                        </div>

                        <div className={"ms-auto"}>
                            <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={toggleInventoryRowConfig} />
                        </div>
                    </div>

                    <WhatsThis subject={"inventory_row_config_blurb"} />
                </div>
            </div>
            <DragDropContext onDragEnd={onDragEnd}>
                <div>
                    <OwTitle title={"selected_columns"} tag={"h5"} />
                    <Droppable
                        droppableId="selectedItems"
                        direction={"horizontal"}
                        isDropDisabled={false}
                        isCombineEnabled={false}
                        ignoreContainerClipping={true}>
                        {(providedOuter, snapshot) => (
                            <div
                                className={"d-flex w-100 droppable-container-row " + (snapshot.isDraggingOver ? "dragging-over" : "")}
                                ref={providedOuter.innerRef}
                                {...providedOuter.droppableProps}>
                                {selectedItems.map((columnDetail, index) => (
                                    <Draggable
                                        draggableId={columnDetail.property}
                                        index={index}
                                        key={columnDetail.property}
                                        isDragDisabled={columnDetail.property === "actions"}>
                                        {(providedInner, snapshot) => (
                                            <div
                                                className={
                                                    "order-row-config-item " + (columnDetail.property === "actions" ? "not-allowed" : "")
                                                }
                                                ref={providedInner.innerRef}
                                                {...providedInner.dragHandleProps}
                                                {...providedInner.draggableProps}>
                                                <div className={"drag-handles"}>
                                                    <i className={"fas fa-grip-horizontal"}></i>
                                                </div>
                                                <div>{/*<OwPill title={`${index}`} skipTranslate={true} />*/}</div>
                                                <div>
                                                    <OwLabel
                                                        title={columnDetail.property}
                                                        noBottomMargin={true}
                                                        leftIconClass={columnDetail.property === "actions" ? "fad fa-lock" : ""}
                                                    />
                                                </div>
                                                <div>
                                                    <OwCheckbox
                                                        label={"supplier_only"}
                                                        checked={columnDetail.isSupplier}
                                                        name={"isSupplier"}
                                                        fn={(e: any) => {
                                                            handleChange(e, index, columnDetail);
                                                        }}
                                                    />
                                                </div>
                                                <div>
                                                    <OwLabel title={"title_override"} noBottomMargin={true} />
                                                    <OwInputWrapper
                                                        name={"titleOverride"}
                                                        value={columnDetail.titleOverride}
                                                        fn={(e: any) => {
                                                            handleTitleOverrideChange(e, index, columnDetail);
                                                        }}
                                                    />
                                                </div>
                                                {/*{providedInner.placeholder}*/}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {providedOuter.placeholder}
                            </div>
                        )}
                    </Droppable>
                </div>
                <div>
                    <OwTitle title={"available_columns"} tag={"h5"} />
                    <Droppable
                        droppableId="availableItems"
                        direction={"horizontal"}
                        isDropDisabled={false}
                        isCombineEnabled={false}
                        ignoreContainerClipping={true}>
                        {(providedOuter, snapshot) => (
                            <div
                                className={"d-flex w-100 droppable-container-row " + (snapshot.isDraggingOver ? "dragging-over" : "")}
                                ref={providedOuter.innerRef}
                                {...providedOuter.droppableProps}>
                                {availableItems.map((columnDetail, index) => (
                                    <Draggable draggableId={columnDetail.property} index={index} key={columnDetail.property}>
                                        {(providedInner) => (
                                            <div
                                                className={"order-row-config-item"}
                                                ref={providedInner.innerRef}
                                                {...providedInner.dragHandleProps}
                                                {...providedInner.draggableProps}>
                                                <div className={"drag-handles"}>
                                                    <i className={"fas fa-grip-horizontal"}></i>
                                                </div>
                                                <div>{/*<OwPill title={`${index}`} skipTranslate={true} />*/}</div>
                                                <div>
                                                    <OwLabel title={columnDetail.property} noBottomMargin={true} />
                                                </div>
                                                {/*{providedInner.placeholder}*/}
                                            </div>
                                        )}
                                    </Draggable>
                                ))}
                                {providedOuter.placeholder}
                            </div>
                        )}
                    </Droppable>
                </div>
            </DragDropContext>
        </div>
    );
};

export default InventoryRowConfig;
